'use client'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import { useGetSearchParamsObj } from '@/src/hooks/srp/searchParams'
import { validSearchQueryDateFormats } from '@/src/utils/date'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

dayjs.extend(customParseFormat)

interface MulticityRoundTripDialogProps {
    open: boolean
    onClose: () => void
    onEdit: () => void
}

const parseToDate = (dateStr: string): dayjs.Dayjs | null => {
    for (const format of validSearchQueryDateFormats) {
        const parsed = dayjs(dateStr, format)
        if (parsed.isValid()) return parsed
    }
    return null
}

export const MulticityRoundTripDialog: React.FC<MulticityRoundTripDialogProps> = ({
    open,
    onClose,
    onEdit
}) => {
    const t = useTranslations('common')
    const locale = useLocale()
    const router = useRouter()
    const { urlQueryObj } = useGetSearchParamsObj()

    const handleSearchRoundTrip = () => {
        const dep1 = (urlQueryObj?.dep1 as string) || ''
        const arr1 = (urlQueryObj?.arr1 as string) || ''
        const depdate1 = (urlQueryObj?.depdate1 as string) || ''
        const depdate2 = (urlQueryObj?.depdate2 as string) || ''
        const origin1 = (urlQueryObj?.origin1 as string) || dep1
        const destination1 = (urlQueryObj?.destination1 as string) || arr1

        const departDate = parseToDate(depdate1)
        const returnDate = parseToDate(depdate2)

        const queryParams = new URLSearchParams()
        queryParams.set('from', dep1)
        queryParams.set('to', arr1)
        queryParams.set('origin', origin1)
        queryParams.set('destination', destination1)
        queryParams.set('adults', (urlQueryObj?.adults as string) || '1')
        queryParams.set('childs', (urlQueryObj?.childs as string) || '0')
        queryParams.set('infants', (urlQueryObj?.infants as string) || '0')
        queryParams.set('class', (urlQueryObj?.class as string) || 'Economy')
        if (departDate) queryParams.set('depart_date', departDate.format('DD/MM/YYYY'))
        if (returnDate) queryParams.set('return_date', returnDate.format('DD/MM/YYYY'))

        onClose()
        router.push(`/${locale}/flights/results?${queryParams.toString()}`)
    }

    const handleEdit = () => {
        onClose()
        onEdit()
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            dir={locale === 'ar' ? 'rtl' : 'ltr'}
        >
            <DialogTitle sx={{ fontWeight: 700 }}>
                {t('roundTripLikeMulticityTitle')}
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary">
                    {t('roundTripLikeMulticityMessage')}
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button variant="outlined" onClick={handleEdit}>
                    {t('editTrip')}
                </Button>
                <Button variant="contained" onClick={handleSearchRoundTrip}>
                    {t('searchRoundTrip')}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default MulticityRoundTripDialog
